import React, { useState, useRef, useEffect } from 'react';
import { ModalSettings } from './settings/SettingsModal';
import { CollectionsModal } from './CollectionModal';
import { FileBrowserComponent } from '../types/files';
import { IExportService } from '../types/exportTypes';

import { useDocumentStore } from '../stores/documentStore';
import { useCollectionStore } from '../stores/collectionStore';
import { useDocumentSelectionStore } from '../stores/documentSelectionStore';
import { ChatHistory } from './ChatHistory';
import { ChatManagementModal } from './chat-management/ChatManagementModal';
import { Documents } from './Documents';
import { SearchModal } from './SearchModal';

interface SideBarProps {
	fileBrowser?: FileBrowserComponent;
	exportService?: IExportService;
	profileMenu?: React.ComponentType;
}

export function SideBar({ fileBrowser, profileMenu: ProfileMenu, exportService }: SideBarProps) {
	const documents = useDocumentStore((s) => s.documents);
	const removeDocument = useDocumentStore((s) => s.removeDocument);
	const collections = useCollectionStore((s) => s.collections);
	const { selectedFilesDoc, selectedCollections, addDocument } = useDocumentSelectionStore();
	const toggleCollection = useDocumentSelectionStore((s) => s.toggleCollection);
	const clearAllSelections = useDocumentSelectionStore((s) => s.clearAllSelections);

	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [isCollectionOpen, setIsCollectionOpen] = useState(false);
	const [isDocumentsExpanded, setIsDocumentsExpanded] = useState(false);
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [isMobileOpen, setIsMobileOpen] = useState(false);
	const [isChatManagementOpen, setIsChatManagementOpen] = useState(false);
	const [exportChatId, setExportChatId] = useState<string | null>(null);
	const [sidebarWidth, setSidebarWidth] = useState(280);
	const [isResizingActive, setIsResizingActive] = useState(false);
	const isResizing = useRef(false);
	const resizeStartX = useRef(0);
	const resizeStartWidth = useRef(0);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isResizing.current) return;
			const delta = e.clientX - resizeStartX.current;
			const newWidth = Math.max(200, Math.min(600, resizeStartWidth.current + delta));
			setSidebarWidth(newWidth);
		};
		const handleMouseUp = () => {
			if (isResizing.current) {
				isResizing.current = false;
				setIsResizingActive(false);
				document.body.style.cursor = '';
				document.body.style.userSelect = '';
			}
		};
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, []);

	const handleResizeMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
		isResizing.current = true;
		setIsResizingActive(true);
		resizeStartX.current = e.clientX;
		resizeStartWidth.current = sidebarWidth;
		document.body.style.cursor = 'col-resize';
		document.body.style.userSelect = 'none';
	};

	const openSettings = () => setIsSettingsOpen(true);
	const closeSettings = () => setIsSettingsOpen(false);

	const toggleCollectionModal = () => {
		setIsCollectionOpen((prevState) => !prevState);
	};

	const toggleDocumentsExpanded = () => {
		setIsDocumentsExpanded((prevState) => !prevState);
	};

	const toggleSearch = () => {
		setIsSearchOpen((prevState) => !prevState);
	};

	const toggleCollapse = () => setIsCollapsed((prev) => !prev);
	const closeMobile = () => setIsMobileOpen(false);

	// Shared full sidebar content used in desktop-expanded and mobile overlay
	const fullSidebarContent = (
		<>
			{!isDocumentsExpanded && (
				<ChatHistory
					exportService={exportService}
					onCloseMobile={isMobileOpen ? closeMobile : undefined}
					setIsChatManagementOpen={setIsChatManagementOpen}
					setExportChatId={setExportChatId}
				/>
			)}

			<Documents
				selectedFilesDoc={selectedFilesDoc}
				documents={documents}
				collections={collections}
				selectedCollections={selectedCollections}
				addDocument={addDocument}
				removeDocument={removeDocument}
				toggleCollection={toggleCollection}
				onOpenCollection={toggleCollectionModal}
				onClearAll={clearAllSelections}
				isExpanded={isDocumentsExpanded}
				onToggleExpanded={toggleDocumentsExpanded}
			/>

			{/* Bottom bar - settings + profile */}
			<div
				className={`flex items-center justify-between mr-5 ml-5 mb-5 ${isDocumentsExpanded ? 'mt-4' : 'mt-auto'}`}
			>
				<i
					className="fas fa-cog text-neutral-100 text-xl cursor-pointer hover:animate-spin-half transition-all duration-1000"
					title="Settings"
					onClick={openSettings}
					data-testid="settings-btn"
				 />
				{ProfileMenu && <ProfileMenu />}
			</div>
		</>
	);

	return (
		<>
			{/* Hamburger button: visible on mobile only (below md breakpoint) */}
			{!isMobileOpen && (
				<button
					className="fixed top-4 left-4 z-[2002] md:hidden bg-neutral-600 text-neutral-100 border-0 rounded-small p-1 px-2 cursor-pointer"
					onClick={() => setIsMobileOpen(true)}
					aria-label="Open sidebar"
					type="button"
				>
					<i className="fas fa-bars text-sm" />
				</button>
			)}

			{/* Desktop sidebar: not displayed on mobile, flex on md+ */}
			<div
				className={`relative top-0 left-0 z-[2001] mt-0 h-full min-h-screen bg-neutral-600 text-neutral-100 max-md:hidden flex flex-col ${
					isCollapsed
						? 'min-w-[60px] w-[60px] transition-all duration-300'
						: isResizingActive
							? ''
							: 'transition-all duration-300'
				}`}
				style={isCollapsed ? undefined : { width: sidebarWidth, minWidth: 200, maxWidth: 600 }}
			>
				{/* Drag-to-resize handle on right edge */}
				{!isCollapsed && (
					<div
						className="absolute right-0 top-0 bottom-0 w-1.5 z-20 cursor-col-resize group"
						onMouseDown={handleResizeMouseDown}
						title="Drag to resize"
					>
						<div className="absolute inset-0 group-hover:bg-red-500/40 active:bg-red-500/60 transition-colors duration-150" />
					</div>
				)}

				{/* Side-edge collapse/expand handle */}
				<button
					onClick={toggleCollapse}
					className="absolute top-1/2 -translate-y-1/2 -right-4 z-10 w-4 h-16 bg-neutral-600 hover:bg-red-500 border-0 rounded-r-medium cursor-pointer flex items-center justify-center transition-colors duration-200"
					title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					type="button"
					style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
				>
					<i className={`fas fa-chevron-${isCollapsed ? 'right' : 'left'} text-neutral-100 text-[8px]`} />
				</button>

				{isCollapsed ? (
					/* Collapsed icon bar */
					<div className="flex flex-col items-center h-full pt-10 pb-4 gap-6">
						<button
							className="bg-transparent border-0 text-neutral-100 cursor-pointer p-2 hover:text-red-400 transition-colors duration-200"
							onClick={() => { setIsChatManagementOpen(true); }}
							title="Chat history"
							aria-label="Chat history"
							type="button"
						>
							<i className="fas fa-history text-lg" />
						</button>
						<button
							className="bg-transparent border-0 text-neutral-100 cursor-pointer p-2 hover:text-red-400 transition-colors duration-200"
							onClick={toggleCollectionModal}
							title="Collections"
							aria-label="Collections"
							type="button"
						>
							<i className="fas fa-folder text-lg" />
						</button>
						<div className="mt-auto flex flex-col items-center gap-4 mb-5">
							<i
								className="fas fa-cog text-neutral-100 text-xl cursor-pointer hover:animate-spin-half transition-all duration-1000"
								title="Settings"
								onClick={openSettings}
								data-testid="settings-btn-collapsed"
								aria-label="Settings"
							 />
							{ProfileMenu && <ProfileMenu />}
						</div>
					</div>
				) : (
					/* Expanded sidebar - full content */
					fullSidebarContent
				)}
			</div>

			{/* Mobile overlay: conditionally rendered when isMobileOpen */}
			{isMobileOpen && (
				<div className="fixed inset-0 z-[2001] md:hidden bg-neutral-600 text-neutral-100 flex flex-col">
					{/* Close button */}
					<button
						className="absolute top-4 left-4 bg-transparent border-0 text-neutral-100 cursor-pointer p-1 hover:text-red-400 transition-colors duration-200"
						onClick={closeMobile}
						aria-label="Close sidebar"
					>
						<i className="fas fa-times text-sm" />
					</button>

					{fullSidebarContent}
				</div>
			)}

			{/* Modals - wrapped in higher z-index to render above sidebar (z-[2001]) */}
			<div className="relative z-[2100]">
				{isSettingsOpen && (
					<ModalSettings closeSettings={closeSettings} />
				)}
				{isCollectionOpen && (
					<CollectionsModal toggleModal={toggleCollectionModal} FileBrowser={fileBrowser} />
				)}
				{isSearchOpen && (
					<SearchModal
						query=""
						onSubmit={() => toggleSearch()}
						onCancel={() => toggleSearch()}
						isOpen={isSearchOpen}
					/>
				)}
				{exportService && (
					<ChatManagementModal
						isOpen={isChatManagementOpen}
						onClose={() => { setIsChatManagementOpen(false); setExportChatId(null); }}
						exportService={exportService}
						selectedChatId={exportChatId || undefined}
					/>
				)}
			</div>
		</>
	);
}
